<script setup lang="ts">
import { IRadioButtons } from '@inkline/inkline'
import FilterPopoverBase from '../modules/FilterPopoverBase.vue'
import type {
  EstateListingEssentialResponse,
  EstateListingResponse,
} from '~/types/api/EstateListing'
import type { useFilterState } from '~/composables/filter/useFilterState'
import { useRangeSliderState } from '~/composables/filter/useRangeSliderState'
import { useFilterPopoverState } from '~/composables/filter/useFilterPopoverState'

const { t } = useI18n()
const props = defineProps<{
  officesResponse: EstateListingResponse | EstateListingEssentialResponse
  filterState: ReturnType<typeof useFilterState>
}>()

const hasPeopleSizeFilter = Object.keys(
  props.filterState.filterValues.value
).some((key: string) => key.startsWith('people_size'))

const sliderRef = ref<HTMLDivElement | null>(null)
const filterPopoverState = useFilterPopoverState()
const rangeSliderState = useRangeSliderState({
  filterState: props.filterState,
  serverFilterValues: props.officesResponse.filter,
  filterKey: hasPeopleSizeFilter ? 'people_size' : 'unit_size',
})

onMounted(() => {
  rangeSliderState.initializeSlider(sliderRef.value)
})

onBeforeUnmount(() => {
  rangeSliderState.destroySlider()
})

const options = ref([
  {
    id: 'unit_size',
    label: t('filter.size.unitSize'),
    suffix: 'm<sup>2</sup>',
  },
  {
    id: 'people_size',
    label: t('filter.size.peopleSize'),
    suffix: t('filter.size.peopleSizeSuffix'),
  },
])

const selectedUnit = ref(rangeSliderState.filterKey.value)
const selectedOption = computed(() => {
  return (
    options.value.find((o) => o.id === selectedUnit.value) || options.value[0]
  )
})

watchEffect(() => {
  let multiplier

  if (selectedUnit.value === 'unit_size') {
    multiplier = 10
  } else {
    multiplier = 0.1
  }

  rangeSliderState.changeFilterKey(selectedOption.value.id, multiplier)
})
</script>

<template>
  <FilterPopoverBase
    :filter-popover-state="filterPopoverState"
    :show-reset-button="rangeSliderState.isFiltered.value"
  >
    <template #trigger-label>{{ selectedOption.label }}</template>

    <template #trigger-value>
      {{ rangeSliderState.dropdownPlaceholderValue.value }}
      <span v-html="selectedOption?.suffix" />
    </template>

    <template #popover-body>
      <div class="c-filter-size">
        <div class="c-filter-size__title">{{ $t('filter.size.title') }}</div>

        <div class="c-filter-size__toggle">
          <IRadioButtons
            v-model="selectedUnit"
            :options="options"
            size="sm"
            color="primary"
          />
        </div>

        <div class="c-filter-size__inputs">
          <IInput
            v-model="rangeSliderState.inputValues.from.value"
            :placeholder="rangeSliderState.inputPlaceholder.value.from"
            size="sm"
            @update:model-value="rangeSliderState.onFromInputUpdate"
            @blur="rangeSliderState.onFromInputBlur"
            @change="rangeSliderState.onFromInputChange"
          >
            <template #suffix
              ><span v-html="selectedOption?.suffix"
            /></template>
          </IInput>

          <IInput
            v-model="rangeSliderState.inputValues.to.value"
            :placeholder="rangeSliderState.inputPlaceholder.value.to"
            size="sm"
            @update:model-value="rangeSliderState.onToInputUpdate"
            @blur="rangeSliderState.onToInputBlur"
            @change="rangeSliderState.onToInputChange"
          >
            <template #suffix
              ><span v-html="selectedOption?.suffix"
            /></template>
          </IInput>
        </div>

        <div class="c-filter-size__slider">
          <div ref="sliderRef" />
        </div>
      </div>
    </template>
  </FilterPopoverBase>
</template>

<style lang="scss">
@import 'nouislider/dist/nouislider.css';
@import '~/assets/styles/lib/nouislider-overrides/overrides';
</style>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-filter-size {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__toggle {
    :deep(.checkable-button-group) {
      --checkable-button-group--button--background: var(
        --color-green-primary-light-2
      );

      padding: 0;
      border: none;
      box-shadow: none;
      margin: 0;

      .button:not(.disabled) {
        &:hover,
        &:focus {
          --checkable-button-group--button--background: var(--color-gray-200);
        }
      }
    }
  }

  &__inputs {
    display: flex;
    gap: 8px;

    :deep(.input) {
      .input-suffix {
        font-size: 14px;
        font-weight: 600;
        padding-left: 0;
        padding-right: 8px;
        border: none;
        min-width: 42px;
        justify-content: flex-end;
      }

      & > input {
        font-size: 16px;
        padding-right: 2px;
        width: 72px;
      }
    }
  }
}
</style>
